/**
 * advanceSummary.ts — the proof-read, one moment before the advance exists.
 *
 * NOT "ARE YOU SURE?". Every particular the loan v7 Schedule states, in the Schedule's own order and
 * under the Schedule's own row labels, as it will print — so a fat-fingered weekly repayment reads
 * as an absurd line rather than as a right-looking number in a form. `contractSummary`'s surface,
 * over one `loans` row, and deliberately its shape: the clause/part vocabulary is imported rather
 * than restated, so the confirm dialog this feeds is the one `ActivateGate` already draws.
 *
 * THE ORDER IS THE PAGE'S, TOP TO BOTTOM. "The Advance" states what the money is for, what was
 * advanced, the interest, the total, the weekly repayment and the day it starts; "The Parties" and
 * the Related agreement row name the Borrower and the Vehicle. An admin holding the rendered PDF
 * beside this dialog matches it line by line, which is the whole reason the labels are the
 * document's rather than the form's.
 *
 * THE DERIVED ROWS COUNT AS NOTHING MISSING, AND THAT IS THE POINT OF `fields: []`. The interest and
 * the total are arithmetic off the amount advanced and the rate; an admin cannot go and fill either
 * one in. Listing them as empty required fields would put "3 fields this advance requires are
 * empty" over a dialog whose one actual gap is an unselected disbursement.
 *
 * THE RATE IS SHOWN AS IT WOULD BE WRITTEN. An empty rate box writes `0` (`loans.interest_rate` is
 * NOT NULL, and the create has always spelled it `parseFloat(…) || 0`), so the proof-read prints 0
 * per cent rather than a destructive dash for a column that cannot be null.
 *
 * PURE, AND REACT-FREE.
 */

import { format } from 'date-fns';

import { businessDay, formatCurrency } from '@/lib/format';
import { stepForMergeField } from '@/lib/agreements/constants';

import type { ClausePart, SummaryClause } from '../activationSummary';
import { advanceMoney } from './advanceArithmetic';
import type { AdvanceDraft } from './advanceCommit';
import { resolveAdvanceArea, type AdvanceArea } from './advanceSteps';
import type { AdvanceSnapshot } from './advanceState';

export interface AdvanceSummaryFact {
    field: string;
    /** The Schedule's own row label. */
    label: string;
    value: string | null;
    area: AdvanceArea;
    /** Filled by the document generator rather than by anybody in this flow. */
    derived?: boolean;
}

export interface AdvanceSummary {
    clauses: SummaryClause[];
    facts: AdvanceSummaryFact[];
    /** The required fields with nothing in them, in reading order. */
    missing: AdvanceSummaryFact[];
    /** The heading over the block, in `INSTRUMENT_HEADINGS`' voice. */
    heading: string;
}

const MISSING = '—';

export const ADVANCE_HEADING = 'The Operational Expense Advance prints';

function part(value: string | null, prefix = '', suffix = ''): ClausePart {
    if (value === null) return { kind: 'missing', text: MISSING };
    return { kind: 'value', text: `${prefix}${value}${suffix}` };
}

function money(amount: number | null | undefined): string | null {
    return amount === null || amount === undefined ? null : formatCurrency(amount);
}

/** A DATE string on the Ghana calendar day it names — never `new Date(iso)`. */
function dateText(value: string | null | undefined): string | null {
    const date = businessDay(value);
    return date ? format(date, 'MMM d, yyyy') : null;
}

function filled(value: string | null | undefined): string | null {
    return value && value.trim() ? value.trim() : null;
}

/**
 * The rate as the Schedule prints it — `FM990.00` with the trailing zeros and the dot trimmed, which
 * is `agreement_merge_data`'s own spelling for `{{loan.interest_rate}}`. 12 prints "12", 12.5 prints
 * "12.5", and 0 prints "0".
 */
function percentText(rate: number): string {
    return rate.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Which area holds a merge field — READ OFF THE DEEP-LINK TABLE, never a second map.
 *
 * `MERGE_FIELD_STEPS` is where this app says where a `loan.` field is edited, and the pre-flight
 * panel on the agreement area routes by it. A private copy here would be a second answer to walk
 * out of step with the first.
 */
function areaFor(field: string): AdvanceArea {
    return resolveAdvanceArea(stepForMergeField(field)) ?? 'disbursement';
}

const FIELD_LABELS: Record<string, string> = {
    'loan.purpose': 'What this advance is for',
    'loan.principal_amount': 'Amount advanced',
    'loan.weekly_instalment': 'Weekly repayment',
    'loan.first_instalment_date': 'First repayment',
};

/**
 * The principal — the DISBURSEMENT's amount, because `loans` carries no amount column at all.
 *
 * THE DRAFT'S FIGURE FIRST, THE SNAPSHOT'S ONLY FOR THE SAME TRANSACTION. The selector hands the
 * page the row it picked, so the draft knows the amount of a transaction the server read has never
 * seen; falling back to the snapshot's transaction where the ids differ would proof-read the money
 * of a disbursement the admin has just replaced.
 */
function principalOf(draft: AdvanceDraft, snapshot: AdvanceSnapshot): number | null {
    if (draft.amount !== null && draft.amount !== undefined) return draft.amount;
    const transaction = snapshot.transaction;
    if (!transaction || transaction.id !== draft.transactionId) return null;
    return transaction.amount ?? null;
}

/**
 * The advance as its Schedule will read it.
 *
 * It takes the DRAFT rather than the snapshot's saved row, because the whole purpose of the surface
 * is the figures that are about to be written — and the snapshot for the parties, because those are
 * facts about the contract that this flow does not edit.
 */
export function advanceSummary(draft: AdvanceDraft, snapshot: AdvanceSnapshot): AdvanceSummary {
    const principal = principalOf(draft, snapshot);
    const rate = draft.interestRate ?? 0;
    const { total, interest } = advanceMoney(principal, rate);

    const clauses: SummaryClause[] = [
        {
            no: 'What this advance is for',
            fields: ['loan.purpose'],
            parts: [part(filled(draft.purpose))],
        },
        {
            /**
             * NO GLOSS ON THIS ONE, BECAUSE THE SCHEDULE PRINTS NONE. The template's row is
             * `| Amount advanced | GHS {{loan.principal_amount}} |` and nothing more, and the whole
             * contract of this module is that an admin can lay the dialog beside the rendered PDF
             * and match it line by line. The explanation belongs where the figure is CHOSEN, and
             * the disbursement area's hint already carries it.
             */
            no: 'Amount advanced',
            fields: ['loan.principal_amount'],
            parts: [part(money(principal))],
        },
        {
            /**
             * ARITHMETIC, SO NO FIELD OF ITS OWN. The figure is the amount advanced and the rate;
             * neither the interest nor the total below is a box anybody can type into, and the
             * Schedule states in the row underneath that the third is the first two added.
             */
            no: 'Interest',
            fields: [],
            parts: [
                part(money(interest)),
                { kind: 'text', text: `, being ${percentText(rate)} per cent of the Amount advanced, charged once when the advance is made.` },
            ],
        },
        {
            no: 'Total amount repayable',
            fields: [],
            parts: [
                part(money(total)),
                { kind: 'text', text: ' — the Amount advanced together with that interest.' },
            ],
        },
        {
            no: 'Weekly repayment',
            fields: ['loan.weekly_instalment'],
            parts: [part(money(draft.weeklyAmount))],
        },
        {
            no: 'First repayment',
            fields: ['loan.first_instalment_date'],
            parts: [
                part(dateText(draft.firstStartsAt)),
                {
                    kind: 'text',
                    text: ', and every Monday after it until the total is repaid in full.',
                },
            ],
        },
    ];

    const vehicleName = filled([snapshot.vehicle.make, snapshot.vehicle.model].filter(Boolean).join(' '));

    /**
     * THE PARTIES AND THE VEHICLE — the Schedule's own five, and the reason they are on a proof-read
     * this flow cannot edit them from: they are render-blocking merge keys, and the last chance
     * anybody has to notice that the wrong person or the wrong car has been named is the moment
     * before the document is generated over them. The block beneath links out to the contract's own
     * driver and vehicle areas, which is where they are fixed.
     */
    const facts: AdvanceSummaryFact[] = [
        {
            field: 'borrower.full_name',
            label: 'Borrower',
            value: filled(snapshot.driver.fullName),
            area: 'agreement',
        },
        {
            field: 'borrower.address',
            label: 'Borrower’s address',
            value: filled(snapshot.driver.address),
            area: 'agreement',
        },
        {
            field: 'borrower.phone',
            label: 'Borrower’s telephone',
            value: filled(snapshot.driver.phone),
            area: 'agreement',
        },
        {
            field: 'vehicle.model',
            label: 'Vehicle',
            value: vehicleName,
            area: 'agreement',
        },
        {
            field: 'vehicle.registration',
            label: 'Registration',
            value: filled(snapshot.vehicle.registration),
            area: 'agreement',
        },
    ];

    const missingClauseFields: AdvanceSummaryFact[] = [];
    for (const clause of clauses) {
        if (!clause.parts.some((piece) => piece.kind === 'missing')) continue;
        for (const field of clause.fields) {
            missingClauseFields.push({
                field,
                label: FIELD_LABELS[field] ?? clause.no,
                value: null,
                area: areaFor(field),
            });
        }
    }

    const missing = [
        ...missingClauseFields,
        ...facts.filter((fact) => !fact.derived && fact.value === null),
    ];

    return { clauses, facts, missing, heading: ADVANCE_HEADING };
}
